// src/oura/components/docs/threads/ApprovalFold.tsx
//
// The doc drawer's Approved fold, extended into the approval record (Docs
// phase 2, lane 4: 2.13 and 2.14) behind the write switch. One row per
// approved question: the client's words and how they came, who resolved it
// and where (in Oura or in the doc), when, and the doc's version then; the
// amber flag when we resolved it without their agreement. A row opens its
// thread. Under the rows: a client's yes a named person recorded outside the
// doc for a publish (H20a), then the approved PDF, filed or ready to file. The
// filing itself is the drawer foot's primary action (fileApproved.tsx), by
// the named person who clicks it.

import type { ReactNode } from "react";
import { ChevronRightIcon, ExternalLinkIcon, FileCheck2Icon } from "lucide-react";
import type { ApprovalRecordRow, ApprovedReport } from "@service/aeo/clientDocsWrite";
import type { DocThread, DocumentDetail, StaffDocument } from "@service/aeo/clientDocs";
import { cn } from "../../../lib/cn";
import { joinDotted } from "../../../lib/oura";
import { AREA_TEXT, ArrowLink, Dot, DrawerFold, DrawerPulse, FOCUS_RING, SURFACE, TYPE, ROW_HOVER } from "@oura/ui";
import { ouraHref } from "../../../nav";
import { docDate, docsFailureText, trimText, type DocsLoad } from "@oura/docs";
import { VIA_WORDS, authorWord, filedForVersion, questionNumber, useApprovalRecord, useRecordedYes, withoutCode } from "./model";

/** A bare resolve by the client has no words of its own: the record says so in its own way. */
const BARE_RESOLVE = /^resolved it$/i;

/** "“Yes.” in the doc · Resolved by Angel in Oura · Thu 1 Oct", with "· v1.0" on an approval of a doc made before #4874. */
function recordMeta(r: ApprovalRecordRow): string {
    const where = r.where === "in_oura" ? "in Oura" : "in the doc";
    const bySelf = r.resolvedBy.side === "client";
    const words = r.clientWords && !(bySelf && BARE_RESOLVE.test(r.clientWords)) ? `“${trimText(r.clientWords, 60)}” ${VIA_WORDS[r.clientVia ?? "doc"]}` : null;
    return joinDotted(words, `Resolved by ${r.resolvedBy.name} ${where}`, docDate(r.at), r.docVersion ? `v${r.docVersion}` : null);
}

/** One row: the question on one line, the record under it, and the flag. The whole row opens the thread. */
function RecordRow({ n, text, meta, flag, onOpen }: { n: number | null; text: string; meta: string; flag: boolean; onOpen: () => void }) {
    return (
        // A block, not a list item: a global rule brings list markers back on list items.
        <li className={cn("block border-t first:border-t-0", SURFACE.hairline)}>
            <button type="button" onClick={onOpen} className={cn("block w-full rounded-md py-2 text-left", ROW_HOVER, FOCUS_RING)}>
                <span className="flex items-center gap-2">
                    <span className={cn(TYPE.tc, "w-6 shrink-0 font-semibold text-gray-500")}>{n ? `Q${n}` : "·"}</span>
                    <span className="min-w-0 flex-1 truncate text-gray-900">{text}</span>
                    <ChevronRightIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
                </span>
                <span className={cn(TYPE.tc, "block break-words pl-8 text-gray-500")}>{meta}</span>
                {flag && (
                    <span className={cn(TYPE.tc, "mt-0.5 flex items-center gap-1.5 pl-8 font-medium text-amber-800")}>
                        <Dot tone="amber" />
                        Resolved without the client&rsquo;s agreement
                    </span>
                )}
            </button>
        </li>
    );
}

/** Filed PDFs of this doc, newest first. */
function reportsOf(reports: readonly ApprovedReport[] | null, docId: string): ApprovedReport[] {
    return (reports ?? []).filter((r) => r.documentId === docId);
}

export function ApprovalFold({
    doc,
    detail,
    businessId,
    reports,
    onOpenThread,
}: {
    doc: StaffDocument;
    detail: DocumentDetail;
    businessId: string;
    /** The client's filed PDFs (B24), read by the sheet for its foot too. */
    reports: DocsLoad<ApprovedReport[]>;
    onOpenThread: (threadId: string) => void;
}) {
    const record = useApprovalRecord(doc.id);
    const threads = detail.threads;
    const rows = record.data?.rows ?? [];
    // The record lists questions; a resolved thread the client raised still shows here, as Phase 1 had it.
    const raised = threads.filter((t) => t.kind === "raised_by_client" && t.resolved);
    const count = record.data ? rows.length + raised.length : threads.filter((t) => t.resolved && (t.kind === "question" || t.kind === "raised_by_client")).length;
    const unagreed = record.data?.unagreed ?? threads.filter((t) => t.resolvedAgreed === false).length;
    const filed = reportsOf(reports.data, doc.id);
    const current = filedForVersion(reports.data, doc);
    const approved = doc.state === "approved";
    const recordedYes = useRecordedYes(businessId, doc.codes);
    if (count === 0 && filed.length === 0 && recordedYes.length === 0) return null;

    const note = unagreed > 0
        ? `${unagreed} without the client's yes`
        : current
            ? "PDF filed"
            : approved
                ? "Ready to file the PDF"
                : null;

    let body: ReactNode;
    if (record.status === "loading") body = <DrawerPulse lines={3} label="Loading the approval record" />;
    else if (!record.data) body = <p className="m-0 text-gray-700">{record.error ? docsFailureText(record.error, "the approval record") : "No approval record yet."}</p>;
    else {
        const textOf = (r: ApprovalRecordRow) => {
            const t = threads.find((x) => x.id === r.threadId);
            return t ? withoutCode(t.text, t.kind) : withoutCode(r.question, "question");
        };
        const numbered = rows.map((r) => ({ r, n: r.number ?? questionNumberOf(r.threadId, threads) })).sort((a, b) => (a.n ?? 999) - (b.n ?? 999));
        body = (
            <ul className="m-0 list-plain p-0">
                {numbered.map(({ r, n }) => (
                    <RecordRow
                        key={r.threadId}
                        n={n}
                        text={textOf(r)}
                        meta={recordMeta(r)}
                        flag={r.flag === "unagreed"}
                        onOpen={() => onOpenThread(r.threadId)}
                    />
                ))}
                {raised.map((t) => (
                    <RecordRow
                        key={t.id}
                        n={null}
                        text={withoutCode(t.text, t.kind)}
                        meta={joinDotted("Raised by the client", t.resolvedBy ? `Resolved by ${authorWord(t.resolvedBy)}` : "Resolved", docDate(t.resolvedAt))}
                        flag={false}
                        onOpen={() => onOpenThread(t.id)}
                    />
                ))}
            </ul>
        );
    }

    return (
        <DrawerFold id="staff-doc.approved" label={`Approved · ${count}`} note={note} remember>
            {body}
            {recordedYes.length > 0 && (
                <ul className={cn("m-0 list-plain space-y-1.5 p-0", count > 0 && "mt-3")}>
                    {recordedYes.map((o) => (
                        <li key={o.taskId} className={cn(TYPE.tc, "flex items-start gap-2 text-gray-700")}>
                            <Dot tone="green" className="mt-[5px]" />
                            <span className="min-w-0 break-words">
                                <span className="text-gray-900">Client&rsquo;s yes recorded for {o.taskId.replace(":", " ").replace(/_/g, " ")}</span>
                                <span className="block text-gray-500">{joinDotted(o.whereClientSaidYes, o.setBy && `by ${o.setBy}`, o.setAt && docDate(o.setAt), o.reason)}</span>
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            {(approved || filed.length > 0) && (
                <div className={cn(SURFACE.inner, "mt-3")}>
                    {current ? null : approved && (
                        <p className={cn(TYPE.tc, "m-0 text-gray-700")}>
                            Every question is approved. File the approved PDF to the client&rsquo;s reports.
                        </p>
                    )}
                    {filed.length > 0 && (
                        <ul className={cn("m-0 list-plain space-y-1.5 p-0", !current && approved && "mt-2")}>
                            {filed.map((r) => (
                                <li key={r.id} className={cn(TYPE.tc, "flex items-start gap-2 text-gray-700")}>
                                    <FileCheck2Icon aria-hidden size={14} className={cn("mt-0.5 shrink-0", AREA_TEXT.work)} />
                                    <span className="min-w-0 break-words">
                                        {joinDotted("Filed to the client's reports", docDate(r.filedAt), `by ${r.filedBy}`)}
                                        <span className="block break-words text-gray-500">{r.fileName}</span>
                                        <span className="flex flex-wrap gap-x-4">
                                            <a href={r.url} target="_blank" rel="noopener noreferrer" className={cn("inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:text-blue-700", FOCUS_RING)}>
                                                Open the filed PDF
                                                <ExternalLinkIcon aria-hidden size={12} />
                                                <span className="sr-only">, opens in a new tab</span>
                                            </a>
                                            {/* The client's Reports are seen as them: the Client tab's View as the merchant. */}
                                            <ArrowLink to={ouraHref("reviews", { role: "oura-staff", businessId })}>See it in the client&rsquo;s reports</ArrowLink>
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {current && (
                        <p className={cn(TYPE.tc, "m-0 mt-1.5 text-gray-500")}>
                            The filed PDF stays exactly as the client approved it. Any change from here goes back to the client for their yes.
                        </p>
                    )}
                </div>
            )}
        </DrawerFold>
    );
}

function questionNumberOf(threadId: string, threads: readonly DocThread[]): number | null {
    const t = threads.find((x) => x.id === threadId);
    return t ? questionNumber(t, threads) : null;
}
